/**
 * When the user last did something in the portal, in ANY open tab of this browser.
 *
 * The idle sign-out and the active-session refresh both read this. Kept per tab only, a
 * doctor working in a second tab left the first one idle, and after 30 minutes that tab
 * signed out the whole browser (docs#239 finding 41). Each tab writes its newest input
 * time to `localStorage`, at most every few seconds, and reads the newest of its own and
 * the shared one.
 *
 * Only a time stamp is stored, never a token. A value in the future is ignored, so a
 * stale or hand-edited entry cannot hold a tab open. Storage failures (private mode,
 * quota) fall back to this tab's own activity, which is what the portal did before.
 */

const STORAGE_KEY = "sr.onboarding.last_activity";
const WRITE_EVERY_MS = 5_000;

/** The input events that count as activity, the same set Mantine's `useIdle` reads. */
export const ACTIVITY_EVENTS = [
  "keydown",
  "mousemove",
  "touchmove",
  "click",
  "scroll",
  "wheel",
] as const;

let localLast = Date.now();
let lastWritten = 0;

/** Record activity in this tab, and share it with the others. */
export function recordActivity(now: number = Date.now()): void {
  localLast = now;
  // abs(): a clock that stepped back must not block writes until it catches up.
  if (Math.abs(now - lastWritten) < WRITE_EVERY_MS) return;
  lastWritten = now;
  try {
    localStorage.setItem(STORAGE_KEY, String(now));
  } catch {
    /* this tab's own activity still counts */
  }
}

/** The newest activity across this browser's portal tabs, as epoch milliseconds. */
export function lastActivityAt(now: number = Date.now()): number {
  let shared = 0;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const value = raw === null ? Number.NaN : Number(raw);
    if (Number.isFinite(value) && value <= now) shared = value;
  } catch {
    /* fall back to this tab */
  }
  return Math.max(localLast, shared);
}

/** Test seam: this tab's activity at `now`, nothing shared. */
export function _resetActivityForTests(now: number = Date.now()): void {
  localLast = now;
  lastWritten = 0;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
